import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Revela su contenido simulando un sobre que se abre.
 *
 * Réplica web del `EnvelopeReveal` de iOS. Estado inicial: un sobre cerrado
 * (cuerpo + solapa triangular). Al tocarlo, `isOpen` pasa a true y el
 * contenido aparece con un cross-fade + scale-up.
 *
 * Pensado para el modo `envelopeOpen`. El contenido se inyecta como `children`,
 * así que puede envolver cualquier cuerpo.
 */
export interface EnvelopeRevealProps {
  children: ReactNode;
  /** Texto del CTA sobre el sobre cerrado. */
  tapLabel?: string;
}

export default function EnvelopeReveal({
  children,
  tapLabel = "Open the letter"
}: EnvelopeRevealProps) {
  const [isOpening, setIsOpening] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleOpen = () => {
    if (isOpening || isOpen) return;
    setIsOpening(true);
    timeoutRef.current = window.setTimeout(() => {
      setIsOpen(true);
    }, 860);
  };

  if (isOpen) {
    return (
      <div className="animate-scale-in" style={{ animationDuration: "0.5s" }}>
        {children}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleOpen}
      aria-label={tapLabel}
      className="flex w-full flex-col items-center gap-5 py-4 text-center"
    >
      <div className={`envelope-stage ${isOpening ? "is-opening" : ""}`}>
        <div className="envelope-glow" aria-hidden />
        <div className="envelope-sparkles" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <div className="envelope-letter" aria-hidden>
          <div className="envelope-letter-inner">
            <div className="envelope-letter-kicker">For your eyes only</div>
            <div className="envelope-letter-title">A little note before the invitation.</div>
            <div className="envelope-letter-lines">
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
        <div className="envelope-shell" aria-hidden>
          <div className="envelope-back" />
          <div className="envelope-flap" />
          <div className="envelope-front" />
          <div className="envelope-fold envelope-fold-left" />
          <div className="envelope-fold envelope-fold-right" />
          <div className="envelope-seal">♡</div>
        </div>
      </div>

      <div className="max-w-sm space-y-2">
        <p className="font-display text-[clamp(2.2rem,7vw,3.6rem)] leading-[0.95] text-[rgb(var(--text))]">
          There&apos;s a letter waiting for you
        </p>
        <p className="mx-auto max-w-xs text-sm leading-6 text-[rgb(var(--subtext)/0.82)] sm:text-base">
          Break the seal and let the invitation unfold like something meant just for you.
        </p>
      </div>

      <span className="btn-primary rounded-full px-6 py-3 text-sm font-semibold tracking-[0.02em]">
        {isOpening ? "Opening..." : tapLabel}
      </span>
    </button>
  );
}
